import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Badge from "@/components/base/Badge";
import CardActions from "@/components/base/CardActions";
import ConfirmDeleteModal from "@/components/base/ConfirmDeleteModal";
import DataTable, { type DataTableColumn } from "@/components/base/DataTable";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import { Input, Select } from "@/components/base/FormControls";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { TYPE_PIECE_LABELS, type TypePieceIdentite } from "@/pages/proprietaire/locataires/types";
import PlatformLocataireForm from "@/pages/super-admin/components/PlatformLocataireForm";
import { usePlatformData } from "@/store/platform/PlatformDataProvider";
import {
  PLATFORM_SCOPE_LABELS,
  platformLocataireInitiales,
  platformLocataireNomComplet,
  type PlatformLocataire,
  type PlatformScope,
} from "@/store/platform/types";
import type { ExportColumn } from "@/utils/export";

type SortKey = "recent" | "nom";

const sortOptions = [
  { value: "recent", label: "Plus récents" },
  { value: "nom", label: "Nom (A → Z)" },
];

export default function SuperAdminLocatairesPage() {
  const { locataires, removeLocataire, countContratsByLocataire } = usePlatformData();

  const [search, setSearch] = useState("");
  const [scopeFilter, setScopeFilter] = useState("");
  const [sort, setSort] = useState<SortKey>("recent");
  const [editing, setEditing] = useState<PlatformLocataire | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PlatformLocataire | null>(null);

  const scopeOptions = [
    { value: "", label: "Tous les espaces" },
    ...(Object.keys(PLATFORM_SCOPE_LABELS) as PlatformScope[]).map((scope) => ({
      value: scope,
      label: PLATFORM_SCOPE_LABELS[scope],
    })),
  ];

  const stats = useMemo(
    () => ({
      total: locataires.length,
      proprietaires: locataires.filter((item) => item.scope === "proprietaire").length,
      agences: locataires.filter((item) => item.scope === "agence").length,
      avecPiece: locataires.filter((item) => Boolean(item.typePiece)).length,
    }),
    [locataires]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const result = locataires.filter((item) => {
      const matchesScope = !scopeFilter || item.scope === scopeFilter;
      const matchesSearch =
        !term ||
        [platformLocataireNomComplet(item), item.telephone, item.email, item.profession, item.adresse]
          .join(" ")
          .toLowerCase()
          .includes(term);
      return matchesScope && matchesSearch;
    });
    return result.sort((a, b) =>
      sort === "nom"
        ? platformLocataireNomComplet(a).localeCompare(platformLocataireNomComplet(b), "fr")
        : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [locataires, search, scopeFilter, sort]);

  const isFiltering = Boolean(search.trim() || scopeFilter);

  const exportColumns: ExportColumn<PlatformLocataire>[] = [
    { header: "Prénom", value: (item) => item.prenom },
    { header: "Nom", value: (item) => item.nom },
    { header: "Téléphone", value: (item) => item.telephone },
    { header: "Email", value: (item) => item.email },
    { header: "Profession", value: (item) => item.profession },
    { header: "Adresse", value: (item) => item.adresse },
    { header: "Espace", value: (item) => PLATFORM_SCOPE_LABELS[item.scope] },
  ];

  const resetFilters = () => {
    setSearch("");
    setScopeFilter("");
  };

  const columns: DataTableColumn<PlatformLocataire>[] = [
    {
      key: "locataire",
      header: "Locataire",
      className: "min-w-[220px]",
      render: (locataire) => (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-50 font-heading text-xs font-semibold text-primary-700">
            {platformLocataireInitiales(locataire)}
          </span>
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-label font-medium text-foreground-950">
              {platformLocataireNomComplet(locataire)}
            </span>
            <span className="truncate text-xs text-foreground-500">
              {locataire.profession || "Profession non renseignée"}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: "scope",
      header: "Espace",
      render: (locataire) => (
        <Badge tone={locataire.scope === "agence" ? "accent" : "primary"}>
          {PLATFORM_SCOPE_LABELS[locataire.scope]}
        </Badge>
      ),
    },
    {
      key: "telephone",
      header: "Téléphone",
      className: "min-w-[150px]",
      render: (locataire) => (
        <span className="text-foreground-700">{locataire.telephone || "Non renseigné"}</span>
      ),
    },
    {
      key: "email",
      header: "Email",
      className: "min-w-[210px]"
      ,
      render: (locataire) => <span className="text-foreground-700">{locataire.email || "Non renseigné"}</span>,
    },
    {
      key: "piece",
      header: "Pièce d'identité",
      className: "min-w-[160px]",
      render: (locataire) => {
        const piece = locataire.typePiece
          ? TYPE_PIECE_LABELS[locataire.typePiece as TypePieceIdentite]
          : "";
        return piece ? (
          <span className="text-foreground-700">
            {piece}
            {locataire.numeroPiece ? ` · ${locataire.numeroPiece}` : ""}
          </span>
        ) : (
          <span className="text-foreground-400">Sans pièce</span>
        );
      },
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "min-w-[200px]",
      render: (locataire) => (
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/super-admin/locataires/${locataire.id}`}
            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-foreground-900 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
          >
            Voir la fiche
            <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
          </Link>
          <CardActions onEdit={() => setEditing(locataire)} onDelete={() => setPendingDelete(locataire)} />
        </div>
      ),
    },
  ];

  const confirmDelete = () => {
    if (!pendingDelete) return;
    removeLocataire(pendingDelete.id);
    setPendingDelete(null);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Locataires</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
            Vue globale de tous les locataires de la plateforme. Un locataire est une entité indépendante ; sa relation
            avec un bien passe par le contrat.
          </p>
        </div>
        <ExportMenu
          rows={filtered}
          columns={exportColumns}
          filename="super-admin-locataires"
          title="Locataires"
          subtitle="Locataires affichés selon les filtres"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-user-3-line" label="Locataires" value={String(stats.total)} tone="primary" />
        <StatCard icon="ri-user-star-line" label="Espace Propriétaire" value={String(stats.proprietaires)} tone="secondary" />
        <StatCard icon="ri-building-4-line" label="Espace Agence" value={String(stats.agences)} tone="accent" />
        <StatCard icon="ri-id-card-line" label="Avec pièce d'identité" value={String(stats.avecPiece)} tone="neutral" />
      </div>

      {locataires.length > 0 && (
        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 lg:max-w-2xl">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="sa-loc-search" className="text-xs font-label font-medium text-foreground-700">
                  Recherche
                </label>
                <Input
                  id="sa-loc-search"
                  icon="ri-search-line"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Nom, téléphone, email..."
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="sa-loc-scope" className="text-xs font-label font-medium text-foreground-700">
                  Espace
                </label>
                <Select
                  id="sa-loc-scope"
                  value={scopeFilter}
                  onChange={(event) => setScopeFilter(event.target.value)}
                  options={scopeOptions}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5 sm:w-52">
              <label htmlFor="sa-loc-sort" className="text-xs font-label font-medium text-foreground-700">
                Trier par
              </label>
              <Select
                id="sa-loc-sort"
                value={sort}
                onChange={(event) => setSort(event.target.value as SortKey)}
                options={sortOptions}
              />
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-200 pt-3">
            <p className="text-xs text-foreground-500">
              {filtered.length} locataire{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""} sur{" "}
              {locataires.length}
            </p>
            {isFiltering && (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
              >
                <i className="ri-refresh-line text-sm" aria-hidden="true" />
                Réinitialiser
              </button>
            )}
          </div>
        </section>
      )}

      {locataires.length === 0 && (
        <EmptyState
          icon="ri-user-3-line"
          title="Aucun locataire"
          description="Les locataires créés par les propriétaires et les agences apparaîtront ici automatiquement."
        />
      )}

      {locataires.length > 0 && filtered.length === 0 && (
        <EmptyState
          icon="ri-search-2-line"
          title="Aucun résultat"
          description="Aucun locataire ne correspond à votre recherche ou au filtre sélectionné."
          action={
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true" />
              Réinitialiser les filtres
            </button>
          }
        />
      )}

      {filtered.length > 0 && (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(locataire) => locataire.id}
          minWidthClass="min-w-[1100px]"
          caption="Locataires"
        />
      )}

      <PlatformLocataireForm open={Boolean(editing)} onClose={() => setEditing(null)} locataire={editing} />

      <ConfirmDeleteModal
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        description={
          pendingDelete
            ? `Supprimer le locataire « ${platformLocataireNomComplet(pendingDelete)} » ? Cette action est définitive.`
            : ""
        }
        blockedMessage={
          pendingDelete && countContratsByLocataire(pendingDelete.id) > 0
            ? `Ce locataire est relié à ${countContratsByLocataire(pendingDelete.id)} contrat(s). Clôturez ou supprimez d'abord ses contrats.`
            : undefined
        }
      />
    </div>
  );
}